import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Actions, Subjects, User } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TrialLimitBanner } from '@/components/users/TrialLimitBanner';
import { useDismissAddUsersBanner } from '@/hooks/query';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { useCreateUserRedirect } from '@/hooks/useCreateUserRedirect';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useIsAtTrialUserLimit } from '@/hooks/useIsAtTrialUserLimit';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser, setCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import {
  PlugIcon,
  TableIcon,
  TranslationStatusBannerCloseIcon,
  UserPlusIcon,
} from '@/utils/helpers';
import {
  canSeeAddUsersBanner,
  canSetUpIntegrationFromBanner,
} from '@/utils/helpers/add-users-banner.helper';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Banner = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--small-padding);
  padding: 50px; /* No matching token — Figma: 50px */
  margin-bottom: var(--semi-big-padding);
  background: var(--beige-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);

  @media (max-width: 1200px) {
    padding: var(--default-padding);
  }
`;

const BannerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--small-padding);
`;

const BannerTitle = styled.h2`
  margin: 0;
  font-size: var(--medium-font-size);
  font-weight: 600;
  line-height: var(--medium-line-height);
  color: var(--purple-dark);
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  min-width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--purple-dark);

  &:hover {
    opacity: 0.7;
  }
`;

const OptionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: var(--small-padding);
`;

const OptionCard = styled.div`
  flex: 1 1 240px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--default-padding-small);
  padding: var(--default-padding-small);
  background: var(--white-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--box-border-radius);
`;

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
  color: var(--purple-dark);
`;

const OptionTitle = styled.h3`
  margin: 0;
  font-size: var(--small-font-size);
  font-weight: 600;
  line-height: var(--small-line-height);
`;

const OptionDescription = styled.p`
  margin: 0;
  font-size: var(--xsmall-font-size);
  font-weight: 400;
  line-height: var(--xsmall-line-height);
  color: var(--gray-medium-dark);
`;

export const AddUsersBanner: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const handleCreateNewRedirect = useCreateUserRedirect();
  const { dismiss } = useDismissAddUsersBanner();
  const [isDismissed, setIsDismissed] = useState(false);

  const { can } = usePermission();
  const { isCurrentlyImpersonating } = useImpersonation();
  const isAtTrialUserLimit = useIsAtTrialUserLimit();

  const isEligible = canSeeAddUsersBanner(currentUser, currentAccount);

  const canSetUpIntegration = canSetUpIntegrationFromBanner(
    currentUser,
    currentAccount,
  );

  const handleDismiss = async () => {
    setIsDismissed(true);

    try {
      const updatedUser = await dismiss();
      dispatch(
        setCurrentUser({ ...currentUser, meta: updatedUser?.meta } as User),
      );
    } catch {
      setIsDismissed(false);
      toast?.error(t('toast.error'), t('users.addOptions.banner.dismissError'));
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await client.users.downloadImportTemplate(currentAccount?.id as string);
    } catch (e) {
      handleAxiosError(e, toast);
    }
  };

  if (
    isDismissed ||
    !isEligible ||
    isCurrentlyImpersonating() ||
    !can(Actions.CREATE, Subjects.USERS)
  ) {
    return null;
  }

  return (
    <Banner data-testid="add-users-banner">
      <BannerHeader>
        <BannerTitle>{t('users.addOptions.banner.title')}</BannerTitle>
        <CloseButton
          type="button"
          onClick={handleDismiss}
          data-testid="add-users-banner-close"
          aria-label={t('users.addOptions.banner.close')}
        >
          <TranslationStatusBannerCloseIcon />
        </CloseButton>
      </BannerHeader>
      <OptionsRow>
        <OptionCard data-testid="add-users-banner-card-manual">
          <OptionText>
            <UserPlusIcon />
            <OptionTitle>{t('users.addOptions.manual.title')}</OptionTitle>
            <OptionDescription>
              {t('users.addOptions.manual.description')}
            </OptionDescription>
          </OptionText>
          {isAtTrialUserLimit ? (
            <TrialLimitBanner
              description={t('user.freeTrial.limitReached.description')}
            >
              <AppButton label={t('users.addOptions.manual.cta')} isDisabled />
            </TrialLimitBanner>
          ) : (
            <AppButton
              label={t('users.addOptions.manual.cta')}
              onClick={handleCreateNewRedirect}
            />
          )}
        </OptionCard>
        <OptionCard data-testid="add-users-banner-card-csv">
          <OptionText>
            <TableIcon />
            <OptionTitle>{t('users.addOptions.csv.title')}</OptionTitle>
            <OptionDescription>
              {t('users.addOptions.csv.description')}
            </OptionDescription>
          </OptionText>
          <AppButton
            label={t('users.addOptions.csv.cta')}
            onClick={handleDownloadTemplate}
          />
        </OptionCard>
        {canSetUpIntegration && (
          <OptionCard data-testid="add-users-banner-card-integration">
            <OptionText>
              <PlugIcon />
              <OptionTitle>
                {t('users.addOptions.integration.title')}
              </OptionTitle>
              <OptionDescription>
                {t('users.addOptions.integration.description')}
              </OptionDescription>
            </OptionText>
            <AppButton
              label={t('users.addOptions.integration.cta')}
              onClick={() =>
                navigate(
                  RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS](),
                )
              }
            />
          </OptionCard>
        )}
      </OptionsRow>
    </Banner>
  );
};
